import { FocusEvent, KeyboardEvent, ReactNode, Ref, useRef, useState } from "react";
import {
	Box,
	Button,
	ClickAwayListener,
	Paper,
	Popper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import QuantityStepperInput from "./QuantityStepperInput";
import { packQuantityTotal } from "./packQuantity";
import { QTY_LIMITS, parseQtyInput, qtyMin, roundQty } from "../util/quantity";
import { Z_INDEX } from "../styles/zIndex";

type StepperProps = {
	value: number;
	onChange: (value: number) => void;
	min?: number;
	max?: number;
	disabled?: boolean;
	size?: "small" | "medium";
	label?: string;
	error?: boolean;
	helperText?: ReactNode;
	id?: string;
	inputRef?: Ref<HTMLInputElement>;
	onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
	onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
	fullWidth?: boolean;
	required?: boolean;
	fractional?: boolean;
};

const parseField = (raw: string, fallback: number, fractional: boolean): number => {
	const parsed = parseQtyInput(raw, fractional);
	return parsed == null ? fallback : parsed;
};

const helperFieldSx = {
	"& .MuiFilledInput-input": {
		textAlign: "center" as const,
		fontWeight: 600,
		MozAppearance: "textfield",
		"&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
			WebkitAppearance: "none",
			margin: 0,
		},
	},
};

const QuantityPackHelper = (props: StepperProps) => {
	const { t } = useTranslation();
	const { onFocus, onKeyDown, disabled, inputRef, max = QTY_LIMITS.max, min: minProp, fractional = false, ...stepperProps } = props;
	const min = minProp ?? qtyMin(fractional);

	const anchorRef = useRef<HTMLDivElement>(null);
	const paperRef = useRef<HTMLDivElement>(null);
	const pointerOpenRef = useRef(false);
	const [open, setOpen] = useState(false);
	const [count, setCount] = useState(1);
	const [pack, setPack] = useState(1);
	const [extra, setExtra] = useState(0);

	const total = packQuantityTotal(count, pack, extra, fractional);
	const totalValid = total != null && total >= min && total <= max;

	const close = () => setOpen(false);

	const seedAndOpen = () => {
		if (disabled) return;
		const value = Number(props.value);
		const safe = Number.isFinite(value) && value > 0 ? value : qtyMin(fractional);
		if (fractional) {
			const whole = Math.trunc(safe);
			setCount(whole > 0 ? whole : 0);
			setPack(1);
			setExtra(roundQty(safe - whole));
		} else {
			setCount(Math.max(0, Math.trunc(safe) || 1));
			setPack(1);
			setExtra(0);
		}
		setOpen(true);
	};

	const isInsideHelper = (node: Node | null) =>
		Boolean(node && (anchorRef.current?.contains(node) || paperRef.current?.contains(node)));

	const handleBlurCapture = (event: FocusEvent) => {
		const next = event.relatedTarget as Node | null;
		// Empty-area clicks inside the paper have no relatedTarget; ClickAwayListener handles those.
		if (!next || isInsideHelper(next)) return;
		close();
	};

	const applyTotal = () => {
		if (!totalValid || total == null) return;
		props.onChange(total);
		close();
	};

	const handleHelperKeyDown = (event: KeyboardEvent) => {
		if (event.key === "Escape") {
			event.preventDefault();
			close();
			return;
		}
		if (event.key === "Enter") {
			event.preventDefault();
			applyTotal();
		}
	};

	return (
		<ClickAwayListener
			mouseEvent="onMouseDown"
			onClickAway={(event) => {
				if (isInsideHelper(event.target as Node)) return;
				close();
			}}
		>
			<Box
				ref={anchorRef}
				onPointerDown={() => {
					pointerOpenRef.current = true;
				}}
				onBlurCapture={handleBlurCapture}
				sx={{ width: props.fullWidth === false ? "auto" : "100%" }}
			>
				<QuantityStepperInput
					{...stepperProps}
					fractional={fractional}
					min={min}
					max={max}
					disabled={disabled}
					inputRef={inputRef}
					onFocus={(event) => {
						// Pointer/click opens the pack helper; Tab should stay on the quantity field.
						if (pointerOpenRef.current && !open) seedAndOpen();
						pointerOpenRef.current = false;
						onFocus?.(event);
					}}
					onKeyDown={(event) => {
						if (event.key === "Escape") close();
						onKeyDown?.(event);
					}}
				/>
				<Popper
					open={open && !disabled}
					anchorEl={anchorRef.current}
					placement="bottom-start"
					modifiers={[
						{ name: "offset", options: { offset: [0, 6] } },
						{ name: "flip", enabled: true },
						{ name: "preventOverflow", options: { padding: 8 } },
					]}
					style={{ zIndex: Z_INDEX.dialogPopover }}
				>
					<Paper
						ref={paperRef}
						elevation={8}
						role="dialog"
						aria-label={t("common:ui.labels.quantity")}
						onKeyDown={handleHelperKeyDown}
						onMouseDown={(event) => {
							const el = event.target as HTMLElement;
							if (!el.closest("input, button, textarea")) {
								event.preventDefault();
							}
						}}
						sx={{
							p: 1.25,
							width: 320,
							maxWidth: "calc(100vw - 24px)",
							borderRadius: 2,
						}}
					>
						<Stack direction="row" spacing={0.75}>
							<TextField
								id="quantityHelperCount"
								size="small"
								variant="filled"
								label={t("common:ui.labels.quantity")}
								type="number"
								value={count}
								onFocus={(event) => event.target.select()}
								onChange={(event) => setCount(parseField(event.target.value, 0, fractional))}
								sx={helperFieldSx}
								fullWidth
							/>
							<TextField
								id="quantityHelperPack"
								size="small"
								variant="filled"
								label={t("common:ui.quantityHelper.pack")}
								type="number"
								value={pack}
								onFocus={(event) => event.target.select()}
								onChange={(event) => setPack(parseField(event.target.value, 1, fractional))}
								sx={helperFieldSx}
								fullWidth
							/>
							<TextField
								id="quantityHelperExtra"
								size="small"
								variant="filled"
								label={t("common:ui.quantityHelper.extra")}
								type="number"
								value={extra}
								onFocus={(event) => event.target.select()}
								onChange={(event) => setExtra(parseField(event.target.value, 0, fractional))}
								sx={helperFieldSx}
								fullWidth
							/>
						</Stack>
						<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }} spacing={1}>
							<Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
								{t("common:ui.quantityHelper.formula", {
									count,
									pack,
									extra,
									total: totalValid && total != null ? total : "—",
								})}
							</Typography>
							<Button
								type="button"
								size="small"
								variant="contained"
								disabled={!totalValid}
								onMouseDown={(event) => event.preventDefault()}
								onClick={applyTotal}
							>
								{t("common:ui.buttons.validate")}
							</Button>
						</Stack>
					</Paper>
				</Popper>
			</Box>
		</ClickAwayListener>
	);
};

export default QuantityPackHelper;
